import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {
  ActionType,
  BillDateDelayFrom,
  CustomStepCategoryEnum,
  ExecutionTypeEnum,
  Funnel,
  FunnelInput,
  FunnelInputTypeEnum,
  StepDurationUnits,
  StepElements,
  StepTimeOptionsLayout,
  StepTimeOptionTypes,
  User,
} from '../../../_models';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {StepEmulatorService} from '../../../_services';
import {FunnelInputFieldsComponent} from '../../../funnel-input';
import {NgxSmartModalService} from 'ngx-smart-modal';
import {FormControlStatus} from '../../../../app/_forms';

@Component({
  selector: 'step-time-options',
  templateUrl: './step-time-options.component.html',
  styleUrls: ['./step-time-options.component.scss'],
  animations: [
    trigger('fadeInOut', [
      state('void', style({ opacity: 0 })),
      transition(':enter', [animate('500ms ease-in', style({ opacity: 1 }))]),
      transition(':leave', [animate('300ms ease-out', style({ opacity: 0 }))]),
    ]),
  ],
})
export class StepTimeOptionsComponent implements OnInit {
  @Input() form: FormGroup;
  @Input() layout: StepTimeOptionsLayout;
  @Input() isNew: boolean;
  @Input() templateId: string;
  @Input() funnel: Funnel;
  @Input() stepType: CustomStepCategoryEnum;
  @Input() user: User;
  @Input() enableFormErrors: boolean;
  @Input() isSingleStepOffer: boolean = false;
  stepElements = StepElements;
  options: {
    value: number;
    unit: StepDurationUnits;
  }[] = [];
  prefixes: string[] = [];
  timeOptionsType: StepTimeOptionTypes;
  stepDurationUnits = StepDurationUnits;
  inputs: FormArray;
  isShippingFreq: boolean;
  isPauseSurvey: boolean;
  billDateDelayFromValue: BillDateDelayFrom;
  public selectedInput: FunnelInput = null;
  selectedInputIndex: number;
  @ViewChild(FunnelInputFieldsComponent, { static: false })
  inputComponent: FunnelInputFieldsComponent;

  constructor(
    protected formBuilder: FormBuilder,
    protected stepEmulatorService: StepEmulatorService,
    public ngxSmartModalService: NgxSmartModalService
  ) {}

  ngOnInit() {
    this.timeOptionsType = this.layout.type;
    this.isShippingFreq = this.timeOptionsType === StepTimeOptionTypes.ShippingFreq;
    this.isPauseSurvey = this.timeOptionsType === StepTimeOptionTypes.PauseOptions;

    if (!this.templateId && this.isNew) {
      const formGroups = this.layout.items.map((item) => {
        return this.getNewInput(item);
      });

      formGroups.push(
        this.formBuilder.group({
          id: null,
          label: [null, Validators.required],
          help: null,
          next_step: null,
          step: null,
          type: FunnelInputTypeEnum.Choice,
          values: this.formBuilder.array([]),
          actions: this.formBuilder.array([]),
          icon: null,
          hide_if_invalid: true,
          product_funnels: [[]],
          classes: [[]],
          is_alt_child_exit: false,
          matched_step_key: null,
          autopick_next_step: null,
          has_response_step: false,
          builder_id: null,
          enter_product_funnel: false,
          enter_troubleshooter: false,
          item_statuses: null,
          button_styles: this.formBuilder.group({
            style: null,
            control_type: null,
            style_type: null,
          }),
        })
      );

      this.options = this.layout.items;
      this.form.setControl('inputs', this.formBuilder.array(formGroups));
    }

    this.inputs = this.form.get('inputs') as FormArray;

    if (!this.isNew && this.inputs) {
      for (let i = 0; i < this.inputs.length; i++) {
        const input = this.inputs.at(i) as FormGroup;
        const actions = input.get('actions') as FormArray;

        if (actions && actions.length > 0) {
          // Check if first action is not a FormGroup (meaning it's raw data)
          const firstAction = actions.at(0);
          if (!(firstAction instanceof FormGroup)) {
            // Reconstruct actions as FormGroups
            const actionsData = actions.value;
            actions.clear();
            actionsData.forEach((actionData) => {
              actions.push(this.formBuilder.group(actionData));
            });
          }
        }
      }
    }

    const inputsToProcess = !this.isSingleStepOffer ? this.inputs.value.slice(0, -1) : this.inputs.value;
    this.prefixes = [];
    this.options = inputsToProcess.map((input) => {
      let value;
      if (this.isShippingFreq) {
        value = input.actions[0].billing_interval_days;
      } else if (this.isPauseSurvey) {
        value = input.actions[0].next_bill_date_delay;
      }

      const unit = input.label.toLowerCase().includes('week') ? StepDurationUnits.Weeks : StepDurationUnits.Days;
      if (unit === StepDurationUnits.Weeks) {
        value = value / 7;
      }

      // Extract the prefix from the label (everything before the first number).
      // If no custom prefix is found, fall back to the default ("Every" / "Pause for").
      const defaultPrefix = this.getDefaultPrefix();
      const labelMatch = input.label ? input.label.match(/^(.*?)\s*\d/) : null;
      const extractedPrefix = labelMatch && labelMatch[1] ? labelMatch[1].trim() : '';
      this.prefixes.push(extractedPrefix || defaultPrefix);

      return {
        value,
        unit,
      };
    });

    this.updateValidatorsForExistingInputs();
    this.initBillDateDelayFrom();
  }

  private initBillDateDelayFrom() {
    this.billDateDelayFromValue = BillDateDelayFrom.Today;
    if (this.inputs && this.inputs.length > 0) {
      const firstInput = this.inputs.at(0);
      const actions = firstInput.get('actions') as FormArray;
      if (actions && actions.length > 0) {
        const action = actions.at(0);
        if (action instanceof FormGroup) {
          const billDateDelayFromControl = action.get('bill_date_delay_from');
          if (billDateDelayFromControl && billDateDelayFromControl.value) {
            this.billDateDelayFromValue = billDateDelayFromControl.value;
          }
        } else {
          const actionValue = action.value;
          if (actionValue && actionValue.bill_date_delay_from) {
            this.billDateDelayFromValue = actionValue.bill_date_delay_from;
          }
        }
      }
    }
  }

  onBillDateDelayFromChange(value: BillDateDelayFrom) {
    if (this.inputs) {
      const inputsCount = !this.isSingleStepOffer ? this.inputs.length - 1 : this.inputs.length;
      for (let i = 0; i < inputsCount; i++) {
        const input = this.inputs.at(i);
        const actions = input.get('actions') as FormArray;
        if (actions && actions.length > 0) {
          const action = actions.at(0);
          if (action instanceof FormGroup) {
            const billDateDelayFromControl = action.get('bill_date_delay_from');
            if (billDateDelayFromControl) {
              billDateDelayFromControl.setValue(value);
            } else {
              action.addControl('bill_date_delay_from', new FormControl(value));
            }
          } else {
            const currentValue = action.value;
            action.setValue({ ...currentValue, bill_date_delay_from: value });
          }
        }
      }
    }
  }

  private updateValidatorsForExistingInputs() {
    if (!this.inputs) {
      return;
    }

    for (let i = 0; i < this.inputs.length; i++) {
      const input = this.inputs.at(i);
      const actions = input.get('actions') as FormArray;

      if (actions && actions.length > 0) {
        const action = actions.at(0) as FormGroup;
        const billingIntervalDaysControl = action.get('billing_interval_days');
        const nextBillDateDelayControl = action.get('next_bill_date_delay');

        if (this.isShippingFreq) {
          if (billingIntervalDaysControl) {
            billingIntervalDaysControl.setValidators([Validators.required, Validators.min(1)]);
            billingIntervalDaysControl.updateValueAndValidity();
          }
          if (nextBillDateDelayControl) {
            nextBillDateDelayControl.clearValidators();
            nextBillDateDelayControl.setValue(0);
            nextBillDateDelayControl.updateValueAndValidity();
          }
        } else if (this.isPauseSurvey) {
          if (billingIntervalDaysControl) {
            billingIntervalDaysControl.clearValidators();
            billingIntervalDaysControl.setValue(null);
            billingIntervalDaysControl.updateValueAndValidity();
          }
          if (nextBillDateDelayControl) {
            nextBillDateDelayControl.setValidators([Validators.required, Validators.min(1)]);
            nextBillDateDelayControl.updateValueAndValidity();
          }
        }
      }
    }
  }

  private getDefaultPrefix(): string {
    let prefix = '';
    if (this.isShippingFreq) {
      prefix = 'Every';
    } else if (this.isPauseSurvey) {
      prefix = 'Pause for';
    }
    return prefix;
  }

  private getUnitLabel(unit: StepDurationUnits, value: number): string {
    switch (unit) {
      case StepDurationUnits.Days:
        return value === 1 ? 'Day' : 'Days';
      case StepDurationUnits.Weeks:
        return value === 1 ? 'Week' : 'Weeks';
      default:
        return '';
    }
  }

  getNewInput(item: any): FormGroup {
    let label,
      unit,
      billing_interval_days,
      billing_interval_days_control,
      next_bill_date_delay,
      next_bill_date_delay_control,
      final_billing_cycle_offset = 0,
      check_last_discount = true,
      bill_now = false,
      bill_date_delay_from = this.isPauseSurvey
        ? this.billDateDelayFromValue || BillDateDelayFrom.Today
        : BillDateDelayFrom.NextBillDate,
      max_bill_date_delay = null;

    unit = this.getUnitLabel(item.unit, item.value);

    if (this.isShippingFreq) {
      label = `Every ${item.value} ${unit}`;
      billing_interval_days = item.unit === StepDurationUnits.Weeks ? item.value * 7 : item.value;
      billing_interval_days_control = [billing_interval_days, [Validators.required, Validators.min(1)]];
      next_bill_date_delay = 0;
      next_bill_date_delay_control = [next_bill_date_delay];
    } else if (this.isPauseSurvey) {
      label = `Pause for ${item.value} ${unit}`;
      billing_interval_days = null;
      billing_interval_days_control = [null];
      next_bill_date_delay = item.unit === StepDurationUnits.Weeks ? item.value * 7 : item.value;
      next_bill_date_delay_control = [next_bill_date_delay, [Validators.required, Validators.min(1)]];
    }

    return this.formBuilder.group({
      label,
      help: null,
      next_step: null,
      type: FunnelInputTypeEnum.Radio,
      values: [[]],
      actions: this.formBuilder.array([
        this.formBuilder.group({
          execution_type: ExecutionTypeEnum.Immediate,
          resourcetype: ActionType.BillingCycle,
          billing_interval_days: billing_interval_days_control,
          check_last_discount,
          pause: false,
          final_billing_cycle_offset,
          bill_date_delay_from,
          bill_now,
          next_bill_date_delay: next_bill_date_delay_control,
          max_bill_date_delay,
        }),
      ]),
      icon: null,
      hide_if_invalid: true,
      product_funnels: [[]],
      classes: [[]],
      is_alt_child_exit: false,
      matched_step_key: null,
      autopick_next_step: null,
      has_response_step: true,
    });
  }

  updatePrefix(index: number) {
    const unit = this.getUnitLabel(Number(this.options[index].unit), this.options[index].value);
    this.inputs.at(index).patchValue({
      label: `${this.prefixes[index]} ${this.options[index].value || '--'} ${unit}`,
    });
  }

  updateTime(index: number) {
    const action = this.inputs.at(index).get('actions') as FormArray;
    const unit = this.getUnitLabel(Number(this.options[index].unit), this.options[index].value);
    const calculatedValue =
      Number(this.options[index].unit) === StepDurationUnits.Weeks
        ? this.options[index].value * 7
        : this.options[index].value;

    const firstAction = action.at(0);
    if (this.isShippingFreq) {
      this.inputs.at(index).patchValue({
        label: `${this.prefixes[index]} ${this.options[index].value || '--'} ${unit}`,
      });

      if (firstAction instanceof FormGroup) {
        firstAction.patchValue({ billing_interval_days: calculatedValue });
      } else {
        firstAction.setValue({ ...firstAction.value, billing_interval_days: calculatedValue });
      }
    } else if (this.isPauseSurvey) {
      this.inputs.at(index).patchValue({
        label: `${this.prefixes[index]} ${this.options[index].value || '--'} ${unit}`,
      });

      if (firstAction instanceof FormGroup) {
        firstAction.patchValue({ next_bill_date_delay: calculatedValue });
      } else {
        firstAction.setValue({ ...firstAction.value, next_bill_date_delay: calculatedValue });
      }
    }
  }

  addItem() {
    const item = {
      value: 1,
      unit: StepDurationUnits.Days,
    };
    this.options.push(item);
    this.prefixes.push(this.getDefaultPrefix());
    this.inputs.insert(this.isSingleStepOffer ? this.inputs.length : this.inputs.length - 1, this.getNewInput(item));
  }

  removeItem(index: number) {
    if (index >= 0 && index < this.inputs.length) {
      this.inputs.removeAt(index);
    }

    this.options = this.options.filter((_, i) => i !== index);
    this.prefixes = this.prefixes.filter((_, i) => i !== index);
  }

  toggleElement(element: StepElements) {
    this.stepEmulatorService.toggleElement(element);
  }

  isElementVisible(element: StepElements): boolean {
    return this.stepEmulatorService.isElementVisible(element);
  }

  openInputsPopup(input, index) {
    this.selectedInput = input;
    this.selectedInputIndex = index;
    setTimeout(() => {
      this.ngxSmartModalService.getModal('pauseActions').open();
    }, 100);
  }

  onCloseInputsPopup() {
    const input = (this.form.get('inputs') as FormArray).at(this.selectedInputIndex) as FormControl;
    const actions = input.get('actions') as FormArray;
    const data = this.inputComponent.getFormData();

    actions.clear();

    data.actions.forEach((action) => {
      actions.push(new FormControl(action));
    });
    this.ngxSmartModalService.getModal('pauseActions').close();
  }

  get inputControls() {
    return (this.form.get('inputs') as FormArray).controls;
  }

  get inputsLength(): number {
    const inputs = this.form.get('inputs') as FormArray;
    return inputs ? inputs.length : 0;
  }

  get settingsInvalidity() {
    let isInvalid = false;
    if (this.enableFormErrors) {
      if (!this.isSingleStepOffer) {
        // Exclude last input (Decline Btn) — its validation is handled by StepButtonComponent
        const inputsToCheck = this.inputs.controls.slice(0, -1);
        isInvalid = inputsToCheck.some((control) => control.status === FormControlStatus.Invalid);
      } else {
        isInvalid = this.inputs.status === FormControlStatus.Invalid;
      }
    }
    return isInvalid;
  }

  get isTypeSelectDropdown() {
    return this.form.get('type').value === FunnelInputTypeEnum.Select;
  }

  get isTypeChoice() {
    return (
      this.form.get('type').value === FunnelInputTypeEnum.Choice ||
      this.form.get('type').value === FunnelInputTypeEnum.Radio
    );
  }

  get billDateDelayFromOptions() {
    return BillDateDelayFrom;
  }
}
